import { useContext, useMemo } from "react";
import { RowSelectionState } from "@tanstack/react-table";
import { Invoice } from "@common/types/invoice";
import { useInvoiceListStatistics } from "@api/invoice.api";
import { PaginatedInvoicesTableContext } from "./PaginatedInvoicesTable.context";

interface UseInvoicesFooterPassingStatisticsParams {
    rowSelection?: RowSelectionState;
}

// Backs the invoices table command bar total/average/count display. Mirrors
// useFooterPassingStatistics for Transactions: when rows are selected, the
// stats are computed client-side over just the selected (loaded) invoices;
// otherwise they come from the server, aggregated over every invoice
// matching the current filters (not just the current page).
export function useInvoicesFooterPassingStatistics({
    rowSelection,
}: UseInvoicesFooterPassingStatisticsParams) {
    const { invoices, filters } = useContext(PaginatedInvoicesTableContext);
    const { data: statistics } = useInvoiceListStatistics({ filters });

    const selectedCount = useMemo(
        () =>
            rowSelection
                ? Object.values(rowSelection).filter(Boolean).length
                : 0,
        [rowSelection],
    );

    return useMemo(() => {
        if (selectedCount === 0) {
            return statistics;
        }

        const selectedInvoices = (invoices ?? []).filter(
            (invoice: Invoice) => rowSelection?.[invoice.id],
        );

        const total = selectedInvoices.reduce(
            (sum, invoice) => sum + invoice.total,
            0,
        );

        // Derive count from the same loaded set the total is summed over, so
        // total/count/average stay mutually consistent. Selection is cleared on
        // page change, so this matches the full selection in practice.
        const count = selectedInvoices.length;

        return {
            total,
            count,
            average: count === 0 ? 0 : total / count,
        };
    }, [selectedCount, statistics, invoices, rowSelection]);
}
